import { useFetchData } from "../../../hooks/useFetch";
import { BaseResponse } from "../../../models/response/base-response";
import { ApiConfig, API_REPORT_ROUTER, API_RECEIPTS_ROUTER, API_CUSTOMER_ROUTER, API_BRANCH_ROUTER } from "../../../services/api-config";

// ==================== TYPES ====================

export interface ReportSummary {
  total_orders: number;
  completed_orders: number;
  cancelled_orders: number;
  total_revenue: number;
  avg_order_value: number;
}

export interface RevenueByDate {
  date: string;
  revenue: number;
  order_count: number;
}

export interface RevenueByBranch {
  branch_id: number;
  revenue: number;
  order_count: number;
}

export interface RevenueByEmployee {
  employee_id: number;
  revenue: number;
  order_count: number;
  avg_value: number;
}

export interface RevenueByHour {
  hour: number;
  revenue: number;
  order_count: number;
}

export interface RevenueByService {
  item_id: number;
  item_type: number;
  revenue: number;
  order_count: number;
}

export interface OrdersByStatus {
  waiting_accept: number;
  waiting_pick_up: number;
  waiting_laundry: number;
  in_laundring: number;
  complete: number;
  ready_delivery: number;
  in_delivery: number;
  cancel: number;
}

export interface DeliveryPerformance {
  driver_id: number;
  total_deliveries: number;
  completed: number;
  cancelled: number;
}

export interface ReportQueryParams {
  from_date?: string; // DD-MM-YYYY
  to_date?: string;   // DD-MM-YYYY
  branch_id?: number;
}

export interface RevenueByDateQueryParams extends ReportQueryParams {
  group_by?: "day" | "month";
}

// ==================== HELPER ====================

const toVietnameseDate = (isoDate?: string): string | undefined => {
  if (!isoDate) return undefined;
  const [year, month, day] = isoDate.split("-");
  return `${day}-${month}-${year}`;
};

const toAccountingDate = (isoDate?: string): string | undefined => {
  if (!isoDate) return undefined;
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
};

// ==================== SERVICE ====================

export const ReportService = {
  /** Tổng hợp: doanh thu, số đơn, giá trị trung bình */
  summary: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<ReportSummary>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: ReportQueryParams = {}) =>
        request({
          endPoint: API_REPORT_ROUTER.API_SUMMARY,
          data: {
            from_date: toVietnameseDate(params.from_date),
            to_date: toVietnameseDate(params.to_date),
            branch_id: params.branch_id,
          },
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /** Doanh thu theo ngày hoặc tháng */
  revenueByDate: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<RevenueByDate[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: RevenueByDateQueryParams = {}) =>
        request({
          endPoint: API_REPORT_ROUTER.API_REVENUE_BY_DATE,
          data: {
            from_date: toVietnameseDate(params.from_date),
            to_date: toVietnameseDate(params.to_date),
            branch_id: params.branch_id,
            group_by: params.group_by || "day",
          },
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /** Doanh thu theo chi nhánh */
  revenueByBranch: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<RevenueByBranch[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: ReportQueryParams = {}) =>
        request({
          endPoint: API_REPORT_ROUTER.API_REVENUE_BY_BRANCH,
          data: {
            from_date: toVietnameseDate(params.from_date),
            to_date: toVietnameseDate(params.to_date),
          },
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /** Doanh thu theo nhân viên */
  revenueByEmployee: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<RevenueByEmployee[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: ReportQueryParams = {}) =>
        request({
          endPoint: API_REPORT_ROUTER.API_REVENUE_BY_EMPLOYEE,
          data: {
            from_date: toVietnameseDate(params.from_date),
            to_date: toVietnameseDate(params.to_date),
            branch_id: params.branch_id,
          },
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /** Doanh thu theo giờ */
  revenueByHour: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<RevenueByHour[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: ReportQueryParams = {}) =>
        request({
          endPoint: API_REPORT_ROUTER.API_REVENUE_BY_HOUR,
          data: {
            from_date: toVietnameseDate(params.from_date),
            to_date: toVietnameseDate(params.to_date),
            branch_id: params.branch_id,
          },
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /** Doanh thu theo dịch vụ */
  revenueByService: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<RevenueByService[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: ReportQueryParams = {}) =>
        request({
          endPoint: API_REPORT_ROUTER.API_REVENUE_BY_SERVICE,
          data: {
            from_date: toVietnameseDate(params.from_date),
            to_date: toVietnameseDate(params.to_date),
            branch_id: params.branch_id,
          },
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /** Số đơn theo trạng thái */
  ordersByStatus: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<OrdersByStatus>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: ReportQueryParams = {}) =>
        request({
          endPoint: API_REPORT_ROUTER.API_ORDERS_BY_STATUS,
          data: {
            from_date: toVietnameseDate(params.from_date),
            to_date: toVietnameseDate(params.to_date),
            branch_id: params.branch_id,
          },
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /** Hiệu suất giao hàng */
  deliveryPerformance: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<DeliveryPerformance[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: ReportQueryParams = {}) =>
        request({
          endPoint: API_REPORT_ROUTER.API_DELIVERY_PERFORMANCE,
          data: {
            from_date: toVietnameseDate(params.from_date),
            to_date: toVietnameseDate(params.to_date),
            branch_id: params.branch_id,
          },
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /** Thu chi tổng hợp (Accounting) */
  incomeOverview: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<any>({
      projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
    });
    return {
      fetch: (params: { from_date?: string; to_date?: string }) =>
        request({
          endPoint: API_RECEIPTS_ROUTER.API_INCOME_OVERVIEW,
          data: {
            from_date: toAccountingDate(params.from_date),
            to_date: toAccountingDate(params.to_date),
          },
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /** Danh sách phiếu thu/chi (Accounting) */
  receiptsList: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<any>({
      projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
    });
    return {
      fetch: (params: { from_date?: string; to_date?: string; type?: number; status?: number; limit?: number; page?: number }) =>
        request({
          endPoint: API_RECEIPTS_ROUTER.API_LIST,
          data: {
            from_date: toAccountingDate(params.from_date),
            to_date: toAccountingDate(params.to_date),
            type: params.type,
            status: params.status,
            limit: params.limit || 20,
            page: params.page || 1,
          },
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /** Danh sách khách hàng (Customer) */
  customersList: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<any>({
      projectId: ApiConfig.PROJECT_ID.CUSTOMER_SERVICE,
    });
    return {
      fetch: (params: { limit?: number; page?: number; sort_by?: string; sort_order?: number; from_date?: string; to_date?: string }) =>
        request({
          endPoint: API_CUSTOMER_ROUTER.API_LIST,
          data: {
            limit: params.limit || 500,
            page: params.page || 1,
            sort_by: params.sort_by,
            sort_order: params.sort_order,
            from_date: params.from_date,
            to_date: params.to_date,
          },
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /** Danh sách chi nhánh (User service) */
  branchList: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<any>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (params: { status?: number; company_id?: number } = { status: 1, company_id: 2000 }) =>
        request({
          endPoint: API_BRANCH_ROUTER.API_LIST,
          data: params,
        }),
      response: baseResponse,
      loading,
      error,
    };
  },
};
